/**
 * RoadClosureReport — dialog for reporting a road closure.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Droplets, Flame, Car, Shield, Construction } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CLOSURE_REASONS = [
  { value: 'flood', label: 'Flooding', icon: Droplets, color: '#3b82f6' },
  { value: 'fire', label: 'Fire', icon: Flame, color: '#ef4444' },
  { value: 'accident', label: 'Accident', icon: Car, color: '#f97316' },
  { value: 'police', label: 'Police', icon: Shield, color: '#8b5cf6' },
  { value: 'structural', label: 'Structural', icon: Construction, color: '#eab308' },
  { value: 'other', label: 'Other', icon: AlertTriangle, color: '#6b7280' },
];

export const RoadClosureReport = ({ open, onClose, userLocation, mapCenter }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) { toast.error('Select a reason'); return; }
    const loc = userLocation || mapCenter;
    if (!loc) { toast.error('Location required — enable GPS or set a postcode'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/crisis/road-closures`, {
        lat: loc.lat, lng: loc.lng,
        reason, description,
      });
      toast.success('Road closure reported');
      setReason(''); setDescription('');
      onClose(true);
    } catch (err) {
      const msg = err?.response?.status === 401 ? 'Please log in to report closures' : 'Failed to report closure';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-sm mx-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Report Road Closure</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Reports the closure at your current GPS location.</p>

          <div className="grid grid-cols-3 gap-2">
            {CLOSURE_REASONS.map(r => {
              const Icon = r.icon;
              const active = reason === r.value;
              return (
                <button
                  key={r.value}
                  onClick={() => setReason(r.value)}
                  className={`flex flex-col items-center gap-1 p-2.5 rounded-xl text-[10px] font-medium border-2 transition-all ${
                    active ? 'text-white border-transparent' : 'text-muted-foreground border-border/40 hover:border-border'
                  }`}
                  style={active ? { backgroundColor: r.color, borderColor: r.color } : {}}
                  data-testid={`closure-reason-${r.value}`}
                >
                  <Icon className="w-5 h-5" />
                  {r.label}
                </button>
              );
            })}
          </div>

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Details (optional)..."
            className="w-full bg-muted/50 border border-border/40 rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none resize-none"
            rows={2}
            data-testid="closure-description"
          />

          <button
            onClick={handleSubmit}
            disabled={submitting || !reason}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition-all"
            style={{ backgroundColor: '#ef4444' }}
            data-testid="closure-submit-btn"
          >
            {submitting ? 'Reporting...' : 'Report Closure'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
